import React, { useState } from 'react';
import { DropdownFilter, FilterArrayType } from 'common/components/Filters';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { useStyles } from './index.styles';
import {
  Checkbox,
  ICheckboxStyles,
  Stack,
  Toggle,
  FontWeights,
  FontSizes,
} from '@fluentui/react';
import { payCycleMode } from 'ap/paymentCycle';
import { PayCycleCommonTypes } from 'ap/paymentCycle/__generated__/PayCycleCommonTypes';
import { formatDropdownOptions } from 'common/utils';
import { useQuery } from '@apollo/client';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import { globalMode } from 'utility/cache/ui';
import COMMON_DATA from '../../../PayCycleCommonTypes.graphql';

interface PaymentAccountingSearchBarProps extends SearchBarProps {
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
  toggleActions: () => void;
  onHistoryCheckbox: () => void;
}
export const PaymentAccountingSearchBar: React.FC<
  PaymentAccountingSearchBarProps
> = ({
  columns,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  urlParams,
  onFilterChange,
  onHistoryCheckbox,
}) => {
  const styles = useStyles();
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const { data: commonData } = useQuery<PayCycleCommonTypes>(COMMON_DATA);

  let checkBoxStyle: ICheckboxStyles = {
    label: {
      fontWeight: FontWeights.bold,
      fontSize: FontSizes.size10,
    },
  };

  const transactionTypeOptions = formatDropdownOptions(
    commonData?.paymentTransactionTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.transactionType!,
      includeAll: true,
    }
  );
  const accountOptions = formatDropdownOptions(
    commonData?.paymentAccounts?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );
  const statusTypeOptions = formatDropdownOptions(
    commonData?.invoiceStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
      includeAll: true,
    }
  );

  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  return (
    <SearchBar
      onFilterClear={() => {}}
      urlParams={urlParams}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      renderActions={
        <Stack
          className={styles.actionsWrapperStyle}
          horizontalAlign="center"
          verticalAlign="center"
          horizontal
          tokens={{ childrenGap: 25 }}
        >
          <Toggle
            label="Pay Cycle"
            inlineLabel
            defaultChecked={true}
            onText="Accounting Entry"
            offText="Accounting Entry"
            onChange={() => {
              globalMode(false);
              payCycleMode(true);
            }}
            className={styles.signingAccountingToggle}
          />
          <Checkbox
            styles={checkBoxStyle}
            label="History"
            onChange={onHistoryCheckbox}
          />
        </Stack>
      }
    >
      <Stack
        verticalAlign="center"
        horizontal
        tokens={{ childrenGap: 20, padding: '0px 0px 0px 20px' }}
      >
        <DropdownFilter
          filterKey={'transactionTypeId'}
          options={transactionTypeOptions}
          placeholder={'Transaction type'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'paymentAccountId'}
          options={accountOptions}
          placeholder={'Bank account'}
          onOptionChange={onOptionChange}
        />
        <DropdownFilter
          filterKey={'_statusTypeId'}
          options={statusTypeOptions}
          placeholder={'Status'}
          onOptionChange={onOptionChange}
        />
      </Stack>
    </SearchBar>
  );
};
